import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { queryCertificate } from '../services/certificateService';
import './CertificateQuery.css';

const CertificateQuery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [certificateNumber, setCertificateNumber] = useState('');
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  // Sertifika sorgulama fonksiyonu
  const performQuery = useCallback(async (number) => {
    if (!number || number.trim().length === 0) {
      return;
    }

    setLoading(true);
    setError(null);
    setCertificate(null);
    setSearched(true);

    try {
      const normalizedNumber = number.trim().toUpperCase();
      const result = await queryCertificate(normalizedNumber);
      if (result) {
        setCertificate(result);
        // URL'i güncelle (QR kod için)
        navigate(`/certificate-query?number=${encodeURIComponent(normalizedNumber)}`, { replace: true });
      } else {
        setError('Certificate not found');
      }
    } catch (err) {
      console.error('Certificate query error:', err);
      setError('An error occurred while querying the certificate. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // URL parametresinden sertifika numarasını oku
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const numberParam = params.get('number');
    if (numberParam && numberParam.trim().length > 0) {
      const normalizedNumber = numberParam.trim().toUpperCase();
      setCertificateNumber(normalizedNumber);
      // Otomatik olarak sorgula
      performQuery(normalizedNumber);
    }
  }, [location.search, performQuery]);

  const handleChange = (e) => {
    // Input'u otomatik olarak büyük harfe çevir
    const value = e.target.value.toUpperCase();
    setCertificateNumber(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!certificateNumber.trim()) {
      setError('Please enter a certificate number');
      return;
    }

    const normalizedNumber = certificateNumber.trim().toUpperCase();
    performQuery(normalizedNumber);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="certificate-query-container">
      <div className="certificate-query-content">
        <div className="certificate-query-main">
          <section className="query-section">
            <h1 className="query-title">Certificate Query</h1>
            <div className="query-description">
              <p>
                You can query the status and validity of certificates issued by NQC (Nova Quality 
                Certification Agency) with the document verification system.
              </p>
            </div>

            <form className="query-form" onSubmit={handleSubmit}>
              <div className="query-input-wrapper">
                <span className="query-icon">🔍</span>
                <input
                  type="text"
                  className="query-input"
                  placeholder="Write the certificate number"
                  value={certificateNumber}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ textTransform: 'uppercase' }}
                />
                <button type="submit" className="query-submit-button" disabled={loading}>
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
            </form>

            {loading && (
              <div className="query-loading">
                <p>Searching for certificate...</p>
              </div>
            )}

            {error && searched && (
              <div className="query-error">
                <p>{error}</p>
              </div>
            )}

            {certificate && (
              <div className="certificate-result">
                <div className="result-header">
                  <h2 className="result-title">Certificate Information</h2>
                  <div className={`status-badge ${certificate.status.toLowerCase()}`}>
                    {certificate.status}
                  </div>
                </div>

                {/* QR Code Section */}
                <div style={{ 
                  marginTop: '20px', 
                  padding: '20px', 
                  backgroundColor: '#f9f9f9', 
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px', color: '#333' }}>
                    Certificate QR Code
                  </h3>
                  <div style={{ 
                    display: 'inline-block', 
                    padding: '15px', 
                    backgroundColor: 'white', 
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <QRCodeSVG 
                      value={`${window.location.origin}/certificate-query?number=${encodeURIComponent(certificate.certificateNumber)}`}
                      size={200}
                      level="H"
                      includeMargin={true}
                    />
                  </div>
                  <p style={{ 
                    marginTop: '10px', 
                    fontSize: '12px', 
                    color: '#666' 
                  }}>
                    Scan this QR code to verify this certificate
                  </p>
                </div>

                <div className="result-details">
                  <div className="detail-row">
                    <span className="detail-label">Certificate Number:</span>
                    <span className="detail-value">{certificate.certificateNumber}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Company Name:</span>
                    <span className="detail-value">{certificate.companyName}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Company Address:</span>
                    <span className="detail-value">{certificate.companyAddress}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Standards:</span>
                    <span className="detail-value">
                      {Array.isArray(certificate.standards) 
                        ? certificate.standards.join(', ')
                        : certificate.standards || 'N/A'}
                    </span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Audit Date:</span>
                    <span className="detail-value">{formatDate(certificate.auditDate)}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Certificate Issue Date:</span>
                    <span className="detail-value">{formatDate(certificate.issueDate)}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Certificate Expiry Date:</span>
                    <span className="detail-value">{formatDate(certificate.expiryDate)}</span>
                  </div>

                  <div className="detail-row">
                    <span className="detail-label">Status:</span>
                    <span className={`detail-value status-text ${certificate.status.toLowerCase()}`}>
                      {certificate.status}
                    </span>
                  </div>

                  {certificate.scope && (
                    <div className="detail-row full-width">
                      <span className="detail-label">Scope:</span>
                      <div className="detail-value scope-text">{certificate.scope}</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        <aside className="query-sidebar">
          <div className="sidebar-section">
            <h2 className="sidebar-title">Our Services</h2>
            <ul className="sidebar-links">
              <li><Link to="/system-certification">System Certification</Link></li>
              <li><Link to="/product-certification">Product Certification</Link></li>
              <li><Link to="/quality-certification">Quality Certification</Link></li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CertificateQuery;

